// The following module serves as an entrypoint for the usage of all 3rd party
// integrations. Any components or libraries should always make use of this
// module instead of interfacing with 3rd party libraries directly. Doing so
// allows our frontend code to remain integration-agnostic.
import config from "@/config";
import { get as lodashGet } from "lodash";
import * as firebase from "@/lib/authentication/firebase/index.js";

// Checks whether the application that loads this module has a specific
// integration configured. Also checks whether all variables required to
// enable the specific integration are present.
function isConfigured(
    integrationType: string,
    integrationName: string,
    locators: string[]
) {
    const integrationConfig = lodashGet(
        config,
        `${integrationType}.${integrationName}`
    );
    // When there is no configuration specified for the the given integration,
    // there is nothing to do.
    if (!integrationConfig) {
        return false;
    }

    // Note, we're returning early through 'some'. As soon as it returns
    // 'true', a value is misconfigured for a certain integration.
    return !locators.some((locator) => {
        const value = lodashGet(integrationConfig, locator);
        // When "%%" exist in the string, either the application is in 'development'
        // mode, in which case there is no need to configure certain integrations
        // or an environment variable hasn't been injected properly.
        if (value.includes("%%")) {
            console.log(
                `Not configuring "${integrationType}.${integrationName}", "${locator}" hasn't been updated. (${value})`
            );
            return true;
        }

        if (!value) {
            console.log(
                `Not configuring "${integrationType}.${integrationName}", "${locator}" hasn't been set.`
            );
            return true;
        }

        return false;
    });
}

const isSegmentConfigured = isConfigured("analytics", "segment", ["writeKey"]);
const isMixPanelConfigured = isConfigured("analytics", "mixpanel", [
    "projectToken",
]);
const isAzureConfigured = isConfigured("authentication", "azure", [
    "clientId",
    "authority",
    "userScope",
]);
const isAuth0Configured = isConfigured("authentication", "auth0", [
    "domain",
    "clientId",
    "redirectUri",
    "audience",
    "scope",
]);
const isFirebaseConfigured = isConfigured("authentication", "firebase", [
    "apiKey",
    "authDomain",
    "databaseURL",
    "projectId",
    "storageBucket",
    "messagingSenderId",
    "appId",
    "measurementId",
]);

// The following is used as a reference to check whether a specific service is
// configured. If so, it can be enabled for this application.
const configured = {
    authentication: {
        basic: !!config?.authentication?.basic,
        fake: !!config?.authentication?.fake,
        azure: isAzureConfigured,
        auth0: isAuth0Configured,
        firebase: isFirebaseConfigured,
    },
    analytics: {
        segment: isSegmentConfigured,
        mixpanel: isMixPanelConfigured,
    },
};

// The following functions are empty, so that they that can still be called
// even though 3rd party analytics have not been initialized for this
// particular app.
// However it still proxies the types in case the integrations are activated
// for the given project.
const analyticsLibraries = {
    segment: {
        page: (() => undefined) as unknown,
        track: (() => undefined) as unknown,
    },
    mixpanel: {
        track: (() => undefined) as unknown,
    },
};

interface IRequiredAuthenticationLibraryExports {
    hasToken: () => Promise<boolean>;
    getToken: (skipStatusUpdate?: boolean) => Promise<string | undefined>;
    signIn: (...args: unknown[]) => Promise<void>;
    signOut: () => Promise<void>;
    events?: EventTarget;
    status: string;
}

// By using the following placeholders, function of authentication libraries
// can be called even though they might not be configured for the application.
//
// These placeholder are (or actually, the references to them in the
// authenticationLibraries object) replaced when the library that they mimic
// is configured and enabled for the application that uses them.
const hasTokenPlaceholder = () => Promise.resolve(false);
const getTokenPlaceholder = (_skipStatusUpdate?: boolean) =>
    Promise.resolve(undefined);
const signInPlaceholder = () => Promise.resolve();
const signOutPlaceholder = () => Promise.resolve();

// The following serves as an intermediary layer that exposes or mimics library
// functions and exports. By using this layer we can easily call any function
// for any library without having to worry about whether the library is actually
// enabled for this application. It also indirectly allows typescript to check
// the types of the exports of the configured the library, making it easier to
// add new authentication libraries into the mix.
const authenticationLibraries: Record<
    string,
    IRequiredAuthenticationLibraryExports
> = {
    firebase: {
        hasToken: hasTokenPlaceholder as typeof firebase.hasToken,
        getToken: getTokenPlaceholder as typeof firebase.getToken,
        signIn: signInPlaceholder as typeof firebase.signIn,
        signOut: signOutPlaceholder as typeof firebase.signOut,
        events: undefined,
        status: undefined,
    },
};
type SupportedAuthenticationLibrary = keyof typeof authenticationLibraries;

// The following replaces the references to the placeholders of library
// functionalities in case the specific authentication library is configured
// for an application.
Object.keys(authenticationLibraries).forEach((libraryName) => {
    // Only initialize authentication libraries that are configured for this
    // project.
    if (!configured.authentication[libraryName]) {
        return;
    }

    Object.keys(authenticationLibraries[libraryName]).forEach(
        (expectedExport) => {
            if (!(expectedExport in firebase)) {
                // This console error shouldn't be exposed to the user, likely only
                // developers will run into this while developing. Nevertheless, the
                // following should actually be captured by a logging library instead.
                // Ticket: components#46
                console.error(
                    `Expected "@/stekz-wc/registry/lib/authentication/${libraryName}/index.js" to export "${expectedExport}" but it doesn't.`
                );
                return;
            }
            authenticationLibraries["firebase"][expectedExport] =
                firebase[expectedExport];
        }
    );
    // Instead of copying over the status, use a getter as a proxy so the
    // status is always in sync with the status of the library.
    Object.defineProperty(authenticationLibraries[libraryName], "status", {
        get: function () {
            return firebase.status;
        },
    });
});

// There can be multiple authentication libraries for a given application,
// however the goal of this function is to figure out which authentication
// library was used by the client to log in. As the following functions for
// authentication libraries "resolve with false" unless they are configured
// in the application it is safe to call each of them. As this happens async,
// return the first promise that resolves.
async function getActiveAuthenticationLibrary(): Promise<SupportedAuthenticationLibrary> {
    const promises = Object.keys(authenticationLibraries).map(
        async (name: SupportedAuthenticationLibrary) => {
            const hasToken = await authenticationLibraries[name].hasToken();
            if (hasToken) {
                return name;
            }

            // No token present. It means that the user still has to log in, or that
            // the user is logged through with a different authentication library.
            return Promise.reject();
        }
    );

    let activeAuthenticationLibrary: SupportedAuthenticationLibrary;
    try {
        activeAuthenticationLibrary = await Promise.any(promises);
    } catch {
        // In this case, the user still has to log in.
        return "";
    }

    return activeAuthenticationLibrary;
}

// The following is the 'only' function in which a reference to used
// authentication library should be passed. After this is called (and the user
// succesfully logged in), the library should expose a token that is used to
// determine whether an authentication library is active.
async function signIn(
    lib?: SupportedAuthenticationLibrary,
    ...args: unknown[]
) {
    if (!lib) {
        // Should use a logging library later #46.
        console.log("Cannot sign in when the library is not provided.");
        return;
    }

    if (!(lib in authenticationLibraries)) {
        throw Error(
            `There is no support for the "${lib}" authentication library.`
        );
    }

    return authenticationLibraries[lib].signIn.apply(null, args);
}

async function signOut() {
    const libraryName = await getActiveAuthenticationLibrary();
    if (libraryName === undefined) {
        return;
    }
    return authenticationLibraries[libraryName].signOut();
}

const authenticationEvents = new EventTarget();
export async function getToken(): Promise<string | undefined> {
    const libraryName = await getActiveAuthenticationLibrary();
    // If there is no library active yet, there is no token to return.
    // In case there is no library active, let listeners know that the status
    // is unauthenticated.
    if (!libraryName) {
        authenticationEvents.dispatchEvent(
            new CustomEvent(LoginStatus.UNAUTHENTICATED)
        );
        return;
    }
    return authenticationLibraries[libraryName].getToken();
}

enum LoginStatus {
    UNAUTHENTICATED = "unauthenticated",
    AUTHENTICATED = "authenticated",
}

// The following adds listeners to events for all configured authentication
// libraries. This includes the libraries that the user can use to log in into
// the application. As there is only one authentication used (at the same time)
// we can safely mimic events from those authentication libraries, giving one
// central event emitter for all consumers (components, libraries) to listen
// to.
Object.keys(authenticationLibraries).forEach((key) => {
    // Don't try to listen to event targets that haven't been initialized. This
    // happens when authentication libraries are not configured for this
    // application.
    if (!authenticationLibraries[key].events) {
        return;
    }

    authenticationLibraries[key].events.addEventListener(
        LoginStatus.AUTHENTICATED,
        () => {
            authenticationEvents.dispatchEvent(
                new CustomEvent(LoginStatus.AUTHENTICATED)
            );
        }
    );
    authenticationLibraries[key].events.addEventListener(
        LoginStatus.UNAUTHENTICATED,
        () => {
            authenticationEvents.dispatchEvent(
                new CustomEvent(LoginStatus.UNAUTHENTICATED)
            );
        }
    );
});

export const authentication = {
    LoginStatus,
    signIn,
    signOut,
    events: authenticationEvents,
    get status() {
        let _status = LoginStatus.UNAUTHENTICATED;
        Object.keys(authenticationLibraries).forEach((key) => {
            if (
                authenticationLibraries[key].status ===
                LoginStatus.AUTHENTICATED
            ) {
                _status = LoginStatus.AUTHENTICATED;
            }
        });
        return _status;
    },
    getToken,
};

function page() {
    analyticsLibraries.segment.page();
    analyticsLibraries.mixpanel.track("Viewed");
}

// The following allows us to use multiple analytics integrations at the same
// time if needed. If they are not configured, the empty function are called
// instead.
function track(
    name: string,
    options?: { [extraInfo: string]: string | number }
) {
    analyticsLibraries.segment.track(name, options);
    analyticsLibraries.mixpanel.track(name, options);
}

export const analytics = {
    page,
    track,
};

function error(description: string, _error?: Error) {
    console.error(description);
}

// Not logging these anywhere yet, just keeping this function as a placeholder
// so we can integrate it with a logging library or service later.
function warning(_description: string) {}

export const monitoring = {
    error,
    warning,
};
